import { toOperationTimestamp } from '@/components/organisms/FishGroupForms';
import { now } from '@/utils/date';

/** The backend accepts a start at most one calendar month back (D21); no upper bound. */
const BACKDATING_MONTHS = 1;

/**
 * The backend's limit (`earliestAllowedStartDate`): the start of the UTC day one calendar
 * month before `reference`, a month end clamped to the shorter month as date-fns does.
 */
export function earliestAllowedStart(reference: Date): Date {
    const year = reference.getUTCFullYear();
    const month = reference.getUTCMonth() - BACKDATING_MONTHS;
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return new Date(Date.UTC(year, month, Math.min(reference.getUTCDate(), daysInMonth)));
}

/**
 * The earliest local calendar day the form can submit: the first one whose start, as the
 * form sends it (`toOperationTimestamp`, noon local for a backdated day), is not before the
 * backend's limit. Depending on the browser's offset from UTC that is the limit's own local
 * day or the one after it.
 */
export function earliestStartDate(reference: Date = now()): Date {
    const limit = earliestAllowedStart(reference).getTime();
    const day = new Date(limit);
    const limitDay = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    const accepted = (candidate: Date) =>
        new Date(toOperationTimestamp(candidate, { reference })).getTime() >= limit;
    return accepted(limitDay)
        ? limitDay
        : new Date(limitDay.getFullYear(), limitDay.getMonth(), limitDay.getDate() + 1);
}

/** A picked or typed start on a calendar day before `earliest`. */
export function isBeforeEarliestStart(startDate: Date, earliest: Date): boolean {
    const day = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const limit = new Date(earliest.getFullYear(), earliest.getMonth(), earliest.getDate());
    return day.getTime() < limit.getTime();
}
